import {useCallback, useState} from 'react';
import {useMutation, useMutationState, useQueryClient} from '@tanstack/react-query';
import {conversationApi} from '../api';
import {Message} from '../model';
import {ConversationAudience} from './useConversationMessages';

// Отправки помечаются в кэше мутаций собственным ключом: ход отправки читается
// оттуда, и по ключу из кэша отбираются только они, а не любые мутации панели.
const SEND_MESSAGE_MUTATION_KEY = ['conversation', 'send-message'];

// Адресат отправки передаётся аргументами мутации, а не берётся из замыкания.
// Композитор при смене Заявки не пересоздаётся, поэтому наблюдатель мутации
// живёт дольше одной переписки, а TanStack Query подменяет параметры уже
// запущенной отправки параметрами текущего рендера. С замыканием отправка,
// приостановленная без сети, после восстановления связи ушла бы в переписку,
// открытую к тому моменту, а запоздавший ответ перечитал бы кэш не той
// Переписки.
export interface SendMessageVariables {
  appealId: string;
  audience: ConversationAudience;
  text: string;
  // Уведомление об успехе едет теми же аргументами, а не вторым параметром
  // mutate: наблюдатель хранит колбэки только последней отправки и от
  // предыдущей отвязывается. У отправки, которую перехватила следующая,
  // переданный в mutate колбэк не сработал бы, и доставленная реплика осталась
  // бы в черновике — с риском уйти адресату второй раз.
  onSent?: () => void;
}

export interface UseSendMessageResult {
  sendMessage: (text: string, onSent?: () => void) => void;
  isSending: boolean;
  isSendError: boolean;
  sendError: Error | null;
}

// Состояние отправки ведётся по Переписке, а не одним значением: отправок
// бывает несколько сразу — приостановленная без сети в одной Переписке и
// начатая следом в другой. Ключ начинается со Стороны: её значения фиксированы
// и двоеточия не содержат, поэтому разбор однозначен.
function sendKeyOf(appealId: string, audience: ConversationAudience): string {
  return `${audience}:${appealId}`;
}

export const useSendMessage = (
  appealId: string,
  audience: ConversationAudience,
): UseSendMessageResult => {
  const queryClient = useQueryClient();
  const [sendErrors, setSendErrors] = useState<Record<string, Error | null>>({});

  const {mutate} = useMutation<Message, Error, SendMessageVariables>({
    mutationKey: SEND_MESSAGE_MUTATION_KEY,
    mutationFn: ({appealId: targetAppealId, audience: targetAudience, text}) => {
      if (targetAudience === 'admin') {
        return conversationApi.sendAdminMessage(targetAppealId, text);
      }
      return conversationApi.sendRepMessage(targetAppealId, text);
    },
    onMutate: (variables) => {
      // Новая попытка снимает отказ своей Переписки и не трогает чужие.
      const sendKey = sendKeyOf(variables.appealId, variables.audience);
      setSendErrors((current) => (current[sendKey] ? {...current, [sendKey]: null} : current));
    },
    onSuccess: (_data, variables) => {
      // Кэш перечитывается по адресату самой отправки: ответ может прийти,
      // когда открыта уже другая переписка.
      queryClient.invalidateQueries({
        queryKey: ['conversation', variables.appealId, 'messages'],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({queryKey: ['conversation', 'admin', 'list'], refetchType: 'all'});

      // Бэкенд сдвигает отметку просмотра отправившей Стороны прямо в момент
      // отправки: своё сообщение никогда не Непрочитанное. Кэш об этом не
      // знает, поэтому оба его следа надо перечитать — состояние просмотра
      // самой Переписки и список, где рисуется признак Непрочитанного. Иначе
      // ушедший сразу после отправки Представитель увидит в списке заявок
      // Непрочитанное по собственной реплике.
      queryClient.invalidateQueries({
        queryKey: ['conversation', variables.appealId, 'seen'],
        refetchType: 'all',
      });
      if (variables.audience === 'representative') {
        queryClient.invalidateQueries({queryKey: ['my-applications'], refetchType: 'all'});
      }

      // Об успехе сообщает сама мутация: её настройки переживают перехват
      // наблюдателя следующей отправкой, поэтому черновик очищается и у той
      // отправки, что оказалась не последней.
      variables.onSent?.();
    },
    onError: (error, variables) => {
      setSendErrors((current) => ({
        ...current,
        [sendKeyOf(variables.appealId, variables.audience)]: error,
      }));
    },
  });

  const sendMessage = useCallback(
    (text: string, onSent?: () => void) => mutate({appealId, audience, text, onSent}),
    [mutate, appealId, audience],
  );

  // Ход отправки читается из кэша мутаций, а не из наблюдателя: наблюдатель
  // помнит только последнюю отправку, и вторая отправка сняла бы замок с
  // композитора первой Переписки — доставленную реплику можно было бы послать
  // второй раз. В кэше видны все отправки сразу, включая приостановленные без
  // сети, и замок держится по своему адресату.
  const pendingSends = useMutationState<SendMessageVariables | undefined>({
    filters: {mutationKey: SEND_MESSAGE_MUTATION_KEY, status: 'pending'},
    select: (pendingSend) => pendingSend.state.variables as SendMessageVariables | undefined,
  });
  const isSending = pendingSends.some(
    (variables) => variables?.appealId === appealId && variables.audience === audience,
  );

  // Отказы помнятся по Переписке по той же причине, но берутся не из кэша
  // мутаций: неудачная отправка лежит в нём до сборки мусора, и после успешной
  // повторной отправки старый отказ всплыл бы снова.
  const sendError = sendErrors[sendKeyOf(appealId, audience)] ?? null;

  return {
    sendMessage,
    isSending,
    isSendError: sendError !== null,
    sendError,
  };
};
